import {
  CUSTOMER_SIGNAL_CONFIG,
  CUSTOMER_SIGNAL,
  SalaryCrossCheckOutcome,
} from '@/constants/signals/customer-signals.constants';
import { RISK_LEVEL } from '@/constants/riskLevels';
import {
  COMPLIANCE_ENTITY_TYPE,
  CHECK_INFO,
  COMPLIANCE_RESULT,
} from '@/constants/businessComplianceAgent';
import { SEVERITY_LEVEL_BY_FRAUD_SIGNAL } from '@/constants/signalNames';
import { iKeyIconType } from '@inscribe/signal';
import { Statuses } from '@/V2/components/status/utils';
import { CustomerInsightResult } from '@/utils/customerInsights';

const CONFIGURED_RISK_SIGNAL_PREFIX = 'CONFIGURED_RISK_SIGNAL_';

export const mapInsightResultToIcon = (result) => {
  const map = {
    [CustomerInsightResult.NOT_APPLICABLE]: 'medium_solid_info_circle',
    [CustomerInsightResult.LOW]: 'medium_solid_check_circle',
    [CustomerInsightResult.MEDIUM]: 'medium_solid_alert_circle',
    [CustomerInsightResult.HIGH]: 'medium_solid_alert_triangle',
    // legacy ones, these should be removed once we remove the uinified-agent-enabled flag
    [CustomerInsightResult.LOW_RISK]: 'medium_solid_check_circle',
    [CustomerInsightResult.HIGH_RISK]: 'medium_solid_alert_triangle',
    [CustomerInsightResult.INCONCLUSIVE]: 'medium_solid_info_circle',
  };
  return map[result];
};

export const mapInsightResultToIconType = (result) => {
  const map = {
    [CustomerInsightResult.NOT_APPLICABLE]: iKeyIconType.NEUTRAL,
    [CustomerInsightResult.LOW]: iKeyIconType.SUCCESS,
    [CustomerInsightResult.MEDIUM]: iKeyIconType.WARNING,
    [CustomerInsightResult.HIGH]: iKeyIconType.ERROR,
    // legacy ones, these should be removed once we remove the uinified-agent-enabled flag
    [CustomerInsightResult.LOW_RISK]: iKeyIconType.SUCCESS,
    [CustomerInsightResult.HIGH_RISK]: iKeyIconType.ERROR,
    [CustomerInsightResult.INCONCLUSIVE]: iKeyIconType.WARNING,
  };
  return map[result];
};

export const mapInsightResultToReadableLabel = (result) => {
  const map = {
    [CustomerInsightResult.NOT_APPLICABLE]: 'Not Applicable',
    [CustomerInsightResult.LOW]: 'Low Risk',
    [CustomerInsightResult.MEDIUM]: 'Medium Risk',
    [CustomerInsightResult.HIGH]: 'High Risk',
    // legacy ones, these should be removed once we remove the uinified-agent-enabled flag
    [CustomerInsightResult.LOW_RISK]: 'Low Risk',
    [CustomerInsightResult.HIGH_RISK]: 'High Risk',
    [CustomerInsightResult.INCONCLUSIVE]: 'Inconclusive',
  };
  return map[result];
};

export const mapInsightResultToStatus = (result) => {
  const map = {
    [CustomerInsightResult.NOT_APPLICABLE]: Statuses.INFO,
    [CustomerInsightResult.LOW]: Statuses.SUCCESS,
    [CustomerInsightResult.MEDIUM]: Statuses.WARNING,
    [CustomerInsightResult.HIGH]: Statuses.ERROR,
    // legacy ones, these should be removed once we remove the uinified-agent-enabled flag
    [CustomerInsightResult.LOW_RISK]: Statuses.SUCCESS,
    [CustomerInsightResult.HIGH_RISK]: Statuses.ERROR,
    [CustomerInsightResult.INCONCLUSIVE]: Statuses.WARNING,
  };
  return map[result];
};

export const mapInsightResultToComplianceResult = (result) => {
  const map = {
    [CustomerInsightResult.NOT_APPLICABLE]: COMPLIANCE_RESULT.NOT_APPLICABLE,
    [CustomerInsightResult.LOW]: COMPLIANCE_RESULT.PASS,
    [CustomerInsightResult.MEDIUM]: COMPLIANCE_RESULT.INCONCLUSIVE,
    [CustomerInsightResult.HIGH]: COMPLIANCE_RESULT.FAIL,
  };
  return map[result];
};

const mapOutcomeToResult = (outcome) => {
  const map = {
    [SalaryCrossCheckOutcome.UNMATCHED]: RISK_LEVEL.HIGH,
    [SalaryCrossCheckOutcome.PARTIALLY_MATCHED]: RISK_LEVEL.MEDIUM,
    [SalaryCrossCheckOutcome.MATCHED]: RISK_LEVEL.LOW,
    [SalaryCrossCheckOutcome.NOT_APPLICABLE]: RISK_LEVEL.NOT_APPLICABLE,
  };
  return map[outcome];
};

const setVerificationValues = (signalObject, rawInsight) => {
  signalObject.hasBadge = true;
  signalObject.icon = mapInsightResultToIcon(signalObject.result);
  signalObject.iconType = mapInsightResultToIconType(signalObject.result);
  signalObject.status = mapInsightResultToStatus(signalObject.result);
  signalObject.value = rawInsight;
  signalObject.badgeLabel = mapInsightResultToReadableLabel(
    signalObject.result,
  );
  signalObject.result = rawInsight.result;
};

const setSalaryCrossCheckValues = (signalObject, rawInsight) => {
  signalObject.hasBadge = true;
  const result = signalObject.result ?? mapOutcomeToResult(rawInsight.outcome);
  signalObject.icon = mapInsightResultToIcon(result);
  signalObject.iconType = mapInsightResultToIconType(result);
  signalObject.status = mapInsightResultToStatus(result);
  signalObject.description = rawInsight.rationale;
  signalObject.badgeLabel = mapInsightResultToReadableLabel(result);
  signalObject.result = result;
};

const setConfiguredRiskSignalValues = (signalObject, rawInsight) => {
  return {
    ...signalObject,
    ...rawInsight,
    label: rawInsight.title,
    description: rawInsight.rationale,
    icon: mapInsightResultToIcon(rawInsight.result),
    iconType: mapInsightResultToIconType(rawInsight.result),
    status: mapInsightResultToStatus(rawInsight.result),
    badgeLabel: mapInsightResultToReadableLabel(rawInsight.result),
    highRisk: rawInsight.result === RISK_LEVEL.HIGH,
    isConfigurable: true,
  };
};

export const formatSignal = (
  signalKey,
  rawInsight,
  customerHasAnyHighRiskDocuments,
) => {
  const signalConfig = CUSTOMER_SIGNAL_CONFIG[signalKey];
  let returnObject = null;

  let signalObject = {
    key: signalKey,
    name: signalKey,
    value: rawInsight,
    _type: 'signal',
    result: rawInsight.result,
  };

  if (signalKey.startsWith(CONFIGURED_RISK_SIGNAL_PREFIX)) {
    return setConfiguredRiskSignalValues(signalObject, rawInsight);
  }

  if (signalConfig) {
    signalObject = {
      ...signalObject,
      ...signalConfig,
      icon: mapInsightResultToIcon(signalObject.result),
      iconType: mapInsightResultToIconType(signalObject.result),
      status: mapInsightResultToStatus(signalObject.result),
      badgeLabel: mapInsightResultToReadableLabel(signalObject.result),
    };

    // verifications
    if (signalConfig.isVerification) {
      setVerificationValues(signalObject, rawInsight);
    }

    // salary cross check
    if (signalKey === CUSTOMER_SIGNAL.SALARY_CROSS_CHECK) {
      setSalaryCrossCheckValues(signalObject, rawInsight);
    }

    if (signalKey === CUSTOMER_SIGNAL.MULTIPLE_DOCUMENT_RISK_SIGNALS) {
      // add just if there are documents with high risk
      if (customerHasAnyHighRiskDocuments) {
        returnObject = signalObject;
      } else {
        returnObject = {};
      }
    } else {
      returnObject = signalObject;
    }
  }
  return returnObject;
};

export const formatCompliance = (rawInsight) => {
  return {
    ...rawInsight,
    icon: mapInsightResultToIcon(rawInsight.result),
    iconType: mapInsightResultToIconType(rawInsight.result),
    badgeLabel: mapInsightResultToReadableLabel(rawInsight.result),
    status: mapInsightResultToStatus(rawInsight.result),
    subtitle:
      CHECK_INFO[COMPLIANCE_ENTITY_TYPE.COMPANY][rawInsight.type]?.subtypes[
        rawInsight.result
      ] || '',
    hasBadge: true,
    _type: 'business_check',
  };
};

export const getSignalTitle = (signal) => {
  if (signal._type === 'signal') {
    return signal.label;
  } else if (signal._type === 'business_check') {
    return signal.title;
  }
  return '';
};

export const getSignalSubtitle = (signal) => {
  if (signal._type === 'signal') {
    return signal.isVerification ? signal.value?.title : signal.description;
  } else if (signal._type === 'business_check') {
    const companySubtypes =
      CHECK_INFO[COMPLIANCE_ENTITY_TYPE.COMPANY][signal.type]?.subtypes;
    const individualSubtypes =
      CHECK_INFO[COMPLIANCE_ENTITY_TYPE.INDIVIDUAL][signal.type]?.subtypes;
    const resultKey = mapInsightResultToComplianceResult(signal.result);
    return (
      companySubtypes?.[resultKey] || individualSubtypes?.[resultKey] || ''
    );
  }
  return '';
};

export const customerHasAnyHighRiskDocuments = (aggregatedDocumentsResults) => {
  const hasHighSeverity = Object.keys(
    aggregatedDocumentsResults?.fraud_results || {},
  ).some((signalKey) => {
    return SEVERITY_LEVEL_BY_FRAUD_SIGNAL[signalKey] === RISK_LEVEL.HIGH;
  });
  return hasHighSeverity;
};
